// imports section Start

import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ORDER_API_01, ORDER_API_02 } from "../../api/ApiModel";
import {
  externaldeleteApi,
  externalgetApi,
} from "../../services/external/externalApis";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Pagination,
} from "@mui/material";
import Header from "../../components/common/Header";
import { useStyles } from "../../assets/styles/Theme";
import DialogConfirm from "../../components/common/DialogueConfirmation";
import {
  CON_STATUS_ACTIVE,
  CON_STATUS_PENDING,
  CON_IS_CASHIER_NO,
  CON_STATUS_DELETED,
} from "../../utils/sys/Constants";
import CheffItems from "../../components/cheff/CheffItems";
import { DIALOG_TITLES, DIALOG_MESSAGES } from "../../utils/sys/Messages";
// imports section End

export default function CheffPlacedOrders() {
  // Hooks start
  const theme = useTheme();
  const styles = useStyles(theme);
  const dispatch = useDispatch();
  const [placedorderItems, setPlacedorderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [currentAction, setCurrentAction] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchCheffPlacedOrders();
  }, []);
  // Hooks end

  // Get user parameters from Redux store
  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, LOCCODE, RESTCODE, USERNAME } = SYS_USER.USER_PARAM || {};

  // Handlers start

  const itemsPerPage = 16; // Number of items per page

  const openDialog = (action, orderCode) => {
    setCurrentAction(action);
    setSelectedOrder(orderCode);

    if (action === "delete") {
      setDialogTitle(DIALOG_TITLES.DELETE);
      setDialogMessage(DIALOG_MESSAGES.DELETE);
    } else if (action === "update") {
      setDialogTitle(DIALOG_TITLES.UPDATE);
      setDialogMessage(DIALOG_MESSAGES.UPDATE);
    }

    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurrentAction(null);
    setSelectedOrder(null);
  };

  const handleDialogConfirm = async () => {
    if (currentAction === "delete") {
      await handleDeleteOrder(selectedOrder);
    } else if (currentAction === "update") {
      await handleUpdateOrder(selectedOrder, "newStatus");
    }
    handleDialogClose();
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
  };

  // Pagination logic for filtered items
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const filteredItems = placedorderItems; // or apply your filter logic here
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };
  // Handlers end

  // API calls start
  const fetchCheffPlacedOrders = async () => {
    try {
      const response = await externalgetApi(ORDER_API_01, "GET", {
        COMPCODE,
        LOCCODE,
        RESTCODE,
        STS: CON_STATUS_PENDING,
        ISCASHIER: CON_IS_CASHIER_NO,
      });
      console.log(response);
      setPlacedorderItems(response);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrder = async (orderCode) => {
    try {
      const response = await externaldeleteApi(ORDER_API_02, "DELETE", {
        COMPCODE,
        LOCCODE,
        RESTCODE,
        ORDERCODE: orderCode,
        STS: CON_STATUS_DELETED,
        ENUSER: USERNAME,
      });

      setPlacedorderItems((prevItems) =>
        prevItems.filter((item) => item.ORDERCODE !== orderCode)
      );
    } catch (error) {
      console.error("Error deleting order:", error);
      if (error.response && error.response.data) {
        console.error("Error details:", error.response.data);
      }
      setError(error);
    }
  };

  // Function to handle order update
  const handleUpdateOrder = async (orderCode, newStatus) => {
    try {
      const response = await externalgetApi(ORDER_API_02, "GET", {
        COMPCODE,
        LOCCODE,
        RESTCODE,
        ORDERCODE: orderCode,
        APPSTS: CON_STATUS_ACTIVE,
        APPUSER: USERNAME,
      });
      console.log("response updtae", response);
      // Update the placedorderItems state with the updated order status
      setPlacedorderItems((prevItems) =>
        prevItems.map((item) =>
          item.ORDERCODE === orderCode ? { ...item, APPSTS: newStatus } : item
        )
      );
      fetchCheffPlacedOrders();
    } catch (error) {
      console.error("Error updating order:", error);
      setError(error);
    }
  };
  // API calls end

  return (
    <Box sx={styles.root}>
      <Grid container sx={styles.height}>
        <Grid
          xs={12}
          sm={12}
          md={12}
          lg={12}
          xl={12}
          sx={styles.screen_content}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              width: "100%",
              padding: "12px 16px 0 16px",
              borderBottom: `2px solid ${theme.palette.stroke.main}`,
              boxShadow: 1,
            }}
          >
            <Header />
          </Box>
          <Box sx={styles.cards_container}>
            <Grid container spacing={4} sx={styles.po_card_item}>
              {loading ? (
                <Box sx={styles.progress_circular}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error">
                  Error fetching data: {error.message}
                </Alert>
              ) : (
                currentItems.map((placedorderItem, index) => (
                  <Grid item xs={12} sm={12} md={6} lg={3} xl={3} key={index}>
                    <CheffItems
                      placedorderItem={placedorderItem}
                      onViewOrder={handleViewOrder}
                      onDeleteOrder={() =>
                        openDialog("delete", placedorderItem.ORDERCODE)
                      }
                      onUpdateOrder={(newStatus) =>
                        openDialog("update", placedorderItem.ORDERCODE)
                      }
                    />
                  </Grid>
                ))
              )}
            </Grid>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                pt: 2,
              }}
            >
              <Pagination
                count={Math.ceil(filteredItems.length / itemsPerPage)}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
      {/* Confirmation Dialog */}
      <DialogConfirm
        open={dialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
        title={dialogTitle}
        message={dialogMessage}
      />
    </Box>
  );
}
